import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import { FlyError } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import type { AxiosInstance, AxiosResponse } from "axios";
import { AxiosError } from "axios";
import { useErrorModal } from "shared-ui";

import {
  FUNDING_APPLICATION_API,
  FUNDING_APPLICATION_STATE_API,
  SUBSEQUENT_FUNDING_APPLICATION_API,
} from "../../components/constants";

export type FormattedFundingApplicationScreen = Omit<
  FundingApplicationSchemas.CustomerFundingApplicationScreen,
  "websiteUrls" | "intendedUse"
> & {
  websiteUrls?: string[];
  intendedUse?: string[];
};

export type FormattedFundingApplicationScreenUpdate = Omit<
  FundingApplicationSchemas.CustomerFundingApplicationUpdate,
  "websiteUrls" | "intendedUse"
> & {
  websiteUrls?: string[];
  intendedUse?: string[];
};

export const useFundingApplication = (customerDomain: string) => {
  const client = useAxios();
  const fetchUrl = FUNDING_APPLICATION_API.replace(":customerSlug", customerDomain);
  const queryFn = () => client.get<FundingApplicationSchemas.CustomerFundingApplicationScreen>(fetchUrl);
  return useSuspenseQuery({
    queryKey: [fetchUrl, customerDomain],
    queryFn,
    select: ({ data }): FormattedFundingApplicationScreen => {
      const urls = data["websiteUrls"] ? data["websiteUrls"].split(",") : [];
      const intendedUses = data["intendedUse"] ? data["intendedUse"].split(",") : [];
      return { ...data, websiteUrls: urls, intendedUse: intendedUses };
    },
    refetchOnMount: "always",
  });
};

const submitFundingApplicationData = (
  client: AxiosInstance,
  mutationUrl: string,
  data: FundingApplicationSchemas.CustomerFundingApplicationUpdate
): Promise<FundingApplicationSchemas.CustomerFundingApplicationScreen> => {
  const url = mutationUrl + data.id.toString();
  return client
    .patch<
      FundingApplicationSchemas.CustomerFundingApplicationUpdate,
      AxiosResponse<FundingApplicationSchemas.CustomerFundingApplicationScreen>
    >(url, data)
    .then(({ data }: AxiosResponse<FundingApplicationSchemas.CustomerFundingApplicationScreen>) => data);
};

// Omit onSuccess when running multiple mutations in parallel
export const useFundingApplicationMutation = (customerDomain: string) => {
  const client = useAxios();
  const queryClient = useQueryClient();
  const mutationUrl = FUNDING_APPLICATION_API.replace(":customerSlug", customerDomain);

  return useMutation({
    mutationKey: ["updateFundingApplication"],
    mutationFn: (data: FormattedFundingApplicationScreenUpdate) => {
      const websiteUrlsStr = data.websiteUrls ? data.websiteUrls.join(",") : undefined;
      const intendedUseStr = data.intendedUse ? data.intendedUse.join(",") : undefined;
      const updatedData: FundingApplicationSchemas.CustomerFundingApplicationUpdate = {
        ...data,
        websiteUrls: websiteUrlsStr,
        intendedUse: intendedUseStr,
      };
      return submitFundingApplicationData(client, mutationUrl, updatedData);
    },
    onSuccess: async () => {
      const useFundingAppQueryKey = [mutationUrl, customerDomain];
      await queryClient.invalidateQueries({ queryKey: useFundingAppQueryKey });
    },
  });
};

export const useFundingApplicationStateMutation = (customerDomain: string) => {
  const client = useAxios();
  const mutationUrl = FUNDING_APPLICATION_STATE_API.replace(":customerSlug", customerDomain);

  return useMutation({
    mutationKey: ["updateFundingApplicationState"],
    mutationFn: (data: FundingApplicationSchemas.CustomerFundingApplicationStateUpdate) => {
      return client.post(mutationUrl, data);
    },
    throwOnError: true,
  });
};

export const useSubmitSubsequentFundingApplication = (customerDomain: string) => {
  const client = useAxios();
  const url = SUBSEQUENT_FUNDING_APPLICATION_API.replace(":customerSlug", customerDomain);
  const { openErrorModal } = useErrorModal();

  return useMutation({
    mutationKey: ["submitSubsequentFundingApplication"],
    mutationFn: (data: FundingApplicationSchemas.SubmitSubsequentFundingApplicationIn) => client.post(url, data),
    onError: (error: AxiosError) => {
      openErrorModal({
        variant: "submit-error",
        error: new Error(`Error starting new subsequent funding application`, { cause: error }),
      });
    },
  });
};

export const useCreateFundingApplicationMutation = (customerDomain: string) => {
  const client = useAxios();
  const mutationUrl = FUNDING_APPLICATION_API.replace(":customerSlug", customerDomain);

  const queryClient = useQueryClient();
  const { openErrorModal, closeErrorModal } = useErrorModal();

  return useMutation({
    mutationKey: ["createNewSalesApplication"],
    mutationFn: () => client.post(mutationUrl),
    onError: (error: AxiosError) => {
      if (error?.status === 403) {
        // occurs when staff members try to create deals from the customer app
        const flyError: FlyError = new FlyError({
          title: "Not permitted",
          message: "Staff users should create new deals using the company page or Deals page in the staff application",
          error: new Error("Error starting new funding application", { cause: error }),
          primaryAction: {
            content: "Close",
            onAction: closeErrorModal,
          },
        });
        openErrorModal(flyError as FlyError);
      } else {
        openErrorModal({
          variant: "submit-error",
          error: new Error("Error starting new funding application", { cause: error }),
        });
      }
    },
    onSuccess: async () => {
      const useFundingAppQueryKey = [mutationUrl, customerDomain];
      await queryClient.invalidateQueries({ queryKey: useFundingAppQueryKey });
    },
  });
};
